'use client';

import { ButtonHTMLAttributes } from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from '@/lib/utils';

/**
 * The `ink` variant is the dark gradient primary used across AI Scribe — the
 * session banners, the header Import CTA, the inline capture control, the
 * binding/override dialog footers and the Scribe home hero (VALE-534).
 *
 * It lives here, not in a feature directory, because the previous arrangement
 * proved it could not stay in one: a shared class-string const
 * (`ai-scribe/session/scribe-buttons.ts`) was introduced to stop the gradient
 * drifting, and five call sites then hand-copied the string past it anyway,
 * arriving at four different heights and two different type sizes. Owning the
 * treatment as a variant is what makes `<Button>` usable on those surfaces at
 * all — converting only their outline/ghost secondaries would have paired a
 * standard button with a hand-rolled primary in the same row.
 *
 * It carries its own rounding (`rounded-md`), type scale (`text-12-5px`) and
 * gap through `compoundVariants`, which cva emits AFTER `size` — a plain
 * variant string would have its `rounded-md` won back by `size`'s `rounded-sm`.
 */
const buttonVariants = cva(
  'inline-flex items-center justify-center whitespace-nowrap rounded-sm text-sm font-medium transition-colors focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring/70 disabled:pointer-events-none disabled:opacity-50 gap-1 cursor-pointer',
  {
    variants: {
      variant: {
        default: 'bg-primary text-primary-foreground hover:bg-primary/90',
        destructive:
          'bg-destructive text-destructive-foreground hover:bg-destructive/90',
        success: 'bg-success text-success-foreground hover:bg-success/90',
        outline:
          'border border-input bg-background hover:bg-accent hover:text-accent-foreground',
        'outline-solid':
          'border border-input bg-background hover:bg-accent hover:text-accent-foreground',
        secondary:
          'bg-secondary text-secondary-foreground hover:bg-secondary/80',
        ghost: 'hover:bg-accent hover:text-accent-foreground',
        link: 'text-primary underline-offset-4 hover:underline',
        ink: 'rounded-md bg-gradient-to-b from-foreground to-foreground-strong font-semibold text-background shadow-sm ring-1 ring-foreground/40 ring-inset transition-all hover:from-foreground/90 hover:to-foreground-strong disabled:opacity-60'
      },
      size: {
        default: 'py-[0.1875rem] px-[0.375rem]',
        xs: 'h-8 rounded-sm px-2.5',
        sm: 'h-9 rounded-sm px-3',
        lg: 'h-11 rounded-sm px-8',
        icon: 'h-10 w-10',
        'icon-xs': 'h-7 w-7'
      }
    },
    compoundVariants: [
      /** Dialog-footer primary — no fixed height, `px-3 py-1.5`. */
      {
        variant: 'ink',
        size: 'default',
        class: 'rounded-md gap-1.5 px-3 py-1.5 text-12-5px'
      },
      /** Header / inline-control primary — `h-8 px-3`. */
      {
        variant: 'ink',
        size: 'xs',
        class: 'rounded-md gap-1.5 px-3 text-12-5px'
      },
      /** Banner CTA — `h-9 px-4`. */
      {
        variant: 'ink',
        size: 'sm',
        class: 'rounded-md gap-1.5 px-4 text-12-5px'
      }
    ],
    defaultVariants: {
      variant: 'default',
      size: 'default'
    }
  }
);

export interface ButtonProps
  extends
    ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = ({
  ref,
  className,
  variant,
  size,
  asChild = false,
  ...props
}: ButtonProps & { ref?: React.Ref<HTMLButtonElement> }) => {
  const Comp = asChild ? Slot : 'button';
  return (
    <Comp
      className={cn(buttonVariants({ variant, size, className }))}
      ref={ref}
      {...props}
    />
  );
};
Button.displayName = 'Button';

export { Button, buttonVariants };
